import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  AssignedContractorPickListDto,
  BuildingDetailDto,
  BuildingPickListItemDto,
  BuildingService,
  ContractorAssignmentMergeDto,
  TeamItemDto,
  TenantFilterDto,
  TenantItemDto,
  TenantService,
} from '@artemis-software/wr-api';
import { FormBuilder } from '@angular/forms';
import {
  AssignmentType,
  PaginatedPickListBehaviour,
} from '@shared/form-controls/generic-pick-list/generic-pick-list.component';
import { firstValueFrom } from 'rxjs';
import { currentTenantId } from '@/utils/admin-utils';
import { NbToastrService } from '@nebular/theme';
import { ActivatedRoute, Router } from '@angular/router';
import { watch } from '@/utils/watch';
import { asyncComputed } from '@/utils/signals/asyncComputed';

@Component({
  standalone: false,
  selector: 'wr-contractor-building-assignment-page',
  templateUrl: './contractor-building-assignment-page.component.html',
  styleUrls: ['./contractor-building-assignment-page.component.scss'],
})
export class ContractorBuildingAssignmentPageComponent implements PaginatedPickListBehaviour<BuildingPickListItemDto> {

  readonly tenantService = inject(TenantService);
  readonly formBuilder = inject(FormBuilder);
  readonly buildingService = inject(BuildingService);
  readonly nbToastrService = inject(NbToastrService);
  readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly isPaginated = true as const;

  readonly currentUrl = this.router.url;

  contractors = asyncComputed(() => {
    const selectedFacilityElement = this.selectedFacilityElement();
    const ownerTenantId = this.ownerTenantId();
    if (selectedFacilityElement == null || ownerTenantId == null) return [];
    return this.tenantService.findAllContractorsByFacilityElementAndExternalTenantId(selectedFacilityElement, ownerTenantId);
  });

  readonly selectedContractor = signal<TenantItemDto | undefined>(undefined);
  readonly selectedFacilityElement = signal<TenantFilterDto.FacilityElementEnum | undefined>(undefined);
  readonly selectedTeam = signal<TeamItemDto | undefined>(undefined);
  readonly ownerTenantId = currentTenantId();
  readonly loading = signal(false);
  readonly selectedBuildingAssignments = signal<BuildingPickListItemDto[]>([]);
  readonly allAssignedBuildingAssignments = signal<BuildingPickListItemDto[]>([]);
  readonly preselectedBuilding = signal<BuildingDetailDto | undefined>(undefined);
  readonly preselectedBuildingSearch = computed(() => {
    const building = this.preselectedBuilding();
    return building?.baseData.sectionText || building?.internalNumber || '';
  });
  readonly preselectionMessage = computed(() => {
    const search = this.preselectedBuildingSearch();
    return search
      ? `Die Gebäudeliste ist auf „${search}“ gefiltert. Gewerk und Dienstleister wählen, dann das Gebäude zuweisen.`
      : undefined;
  });

  async getAllItemsWithPagination(params?: { page?: number; size?: number; search?: string })
    : Promise<{ items: BuildingPickListItemDto[]; total: number }> {
    const ownerTenantId = this.ownerTenantId();

    const items = await firstValueFrom(
      this.buildingService.findAll({
        ownerTenantId: ownerTenantId,
        deleted: false,
        page: params?.page,
        size: params?.size,
        fullTextSearch: params?.search,
        sort: { sort: 'sectionText', order: 'ASC' },
      }),
    );
    return ({
      items,
      total: items.length,
    });
  }

  constructor() {
    void this.loadPreselectedBuilding(this.route.snapshot.queryParamMap.get('buildingId'));

    watch(this.selectedFacilityElement, () => {
      this.selectedContractor.set(undefined);
      this.selectedTeam.set(undefined);
      this.selectedBuildingAssignments.set([]);
      this.allAssignedBuildingAssignments.set([]);
    });

    effect(async () => {
      const contractor = this.selectedContractor();
      const facilityElement = this.selectedFacilityElement();
      const ownerTenantId = this.ownerTenantId();

      if (!contractor?.id || !facilityElement || !ownerTenantId) {
        this.allAssignedBuildingAssignments.set([]);
        this.selectedBuildingAssignments.set([]);
        return;
      }

      this.loading.set(true);
      try {
        const dto: AssignedContractorPickListDto = {
          ownerTenantId: ownerTenantId,
          facilityElement: facilityElement,
          contractorId: contractor.id,
        };

        const buildings = await firstValueFrom(
          this.buildingService.findAllByOwnerTenantAndContractorAndFacilityElement(dto)
        );

        this.allAssignedBuildingAssignments.set(buildings);

        this.selectedBuildingAssignments.set(buildings);

      } finally {
        this.loading.set(false);
      }
    });

  }

  private async loadPreselectedBuilding(buildingId: string | null): Promise<void> {
    if (!buildingId) {
      return;
    }
    this.preselectedBuilding.set(await firstValueFrom(this.buildingService.findById(buildingId)));
  }

  getAssignmentType(item: BuildingPickListItemDto): AssignmentType {
    const assignedIds = this.allAssignedBuildingAssignments().map(b => b.id);

    if (assignedIds.includes(item.id)) {
      return AssignmentType.ASSIGNED;
    }

    return AssignmentType.UNASSIGNED;
  }

  getDisplayName(item: BuildingPickListItemDto): string {
    return item.baseData.sectionText || 'Kein Name vergeben';
  }

  alwaysShowAssignedFromOthers(): boolean {
    return true;
  }

  getFirstAdditionalInfo(item: BuildingPickListItemDto): string {
    return item.baseData.externalNumber || 'Keine interne Nummer';
  }

  getSecondAdditionalInfo(item: BuildingPickListItemDto): string {
    const area = item.baseData?.area;

    if (area == null) {
      return 'Keine Angabe';
    }

    const formatted = new Intl.NumberFormat('de-AT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(area);

    return `${formatted} m²`;
  }

  async submit(): Promise<void> {
    const contractor = this.selectedContractor();
    const facilityElement = this.selectedFacilityElement();
    const assignedBuildings = this.selectedBuildingAssignments();

    if (!contractor?.id || !facilityElement) {
      this.nbToastrService.danger('Bitte wählen Sie einen Dienstleister und ein Gewerk aus.', 'Fehler');
      return;
    }

    this.loading.set(true);

    try {
      const dto: ContractorAssignmentMergeDto = {
        contractorId: contractor.id,
        facilityElement: facilityElement,
        assignedBuildingIds: assignedBuildings?.map(b => b.id),
      };
      await firstValueFrom(this.tenantService.mergeContractorAssignment(dto));

      this.nbToastrService.success('Gebäude wurden erfolgreich dem Dienstleister zugewiesen.', 'Erfolg');
    } catch (error) {
      this.nbToastrService.danger('Fehler beim Speichern der Zuweisungen.', 'Fehler');
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  async assignByTeam(): Promise<void> {
    const team = this.selectedTeam();
    const ownerTenantId = this.ownerTenantId();
    if (!team?.id || !ownerTenantId) return;

    this.loading.set(true);
    try {
      const teamBuildings = await firstValueFrom(
        this.buildingService.findAll({
          ownerTenantId,
          teamId: team.id,
          deleted: false,
        }),
      ) as unknown as BuildingPickListItemDto[];

      this.selectedBuildingAssignments.update(current => {
        const existingIds = new Set(current.map(b => b.id));
        const newBuildings = teamBuildings.filter(b => !existingIds.has(b.id));
        return [...current, ...newBuildings];
      });
    } finally {
      this.loading.set(false);
    }
  }

  async cancel(): Promise<void> {
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigateByUrl(this.currentUrl);
  }
}
